/**
 * @description Main dialog component for creating, editing, validating, and submitting French Electronic Healthcare Invoices (Feuille de Soin Électronique - FSE).
 * @usageNotes Opened as a MatDialog from the main application or host with DialogData containing order context, station configuration, and recipient information.
 * Depends on MercalinkFselibService, MercalinkAPIService, StateService, ApiclientService, ValuesService, and sub-dialog components (Beneficiaire, Recipient, Prescription, Infoadri, Cps, Prestations, Pec, Attach, NatAssu).
 */

import { Component, inject, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';

import { Observable, Subscription, catchError, map, of, skip, switchMap, take, tap, throwError } from 'rxjs';
import Swal from 'sweetalert2';

import { MercalinkFselibService, MercalinkAPIService } from '../mercalink-fselib.service';
import { BeneficiaireComponent } from '../beneficiaire/beneficiaire.component';
import { RecipientComponent } from '../recipient/recipient.component';
import { PrescriptionComponent } from '../prescription/prescription.component';
import { InfoadriComponent } from '../infoadri/infoadri.component';
import { CpsComponent } from '../cps/cps.component';
import { PrestationsComponent, PrestationsDialogResult } from '../prestations/prestations.component';
import { PecComponent } from '../pec/pec.component';
import { AttachComponent } from '../attach/attach.component';
import { NatAssuComponent, DialogData as NatassuDialogData } from '../nat-assu/nat-assu.component';
import { StateService } from '../shared/state.service';
import { ValuesService } from '../shared/values.service';
import * as apiclient from '../shared/apiclient.service';
import * as dao from '../shared/dao';
import * as utils from '../shared/utils';

import * as constants from '../shared/constants';

export * from '../shared/apiclient.service';

export interface InitialPatientData {
  nom?: string;
  prenom?: string;
  nir?: string;
  dateNaissance?: string; // FR format DD/MM/YYYY
  dateNaissanceFse?: string; // FSE format YYYYMMDD
  rangNaissance?: number;
  qualite?: number;
  codeRegime?: string;
  caisseGest?: string;
  centreGest?: string;
  inFsePatient?: boolean;
}

export interface DialogData {
  server_url: string;
  scanapcv_url: string; // Qr code scanner API endpoint
  area_baseurl: string;
  order_id: number;
  fse_id?: number; // undefined means new FSE
  userid: number;
  outputDialogData: OutputDialogData; // Output data from recipient selector
  attachMode: boolean; // Opened in "attachment mode" => open attachment dialog
  station: dao.Station | undefined; // Active station
  configarea: dao.ConfigArea;
  isLocation?: boolean;
  initialPatientData?: InitialPatientData;
}

// Structure returned by recipients component
export interface OutputDialogData {
  // WARNING Must be in sync w/ recipients component
  mode: number; // mode:0 CV, 1 ApCV QR code, 2 ApCV NFC, 3 NO CV=dégradé
  index?: number;
  numIdentFact: string;
  empreinteCV?: dao.EmpreinteCV;
  contexteAuthentificationApCV?: dao.ContexteAuthentificationApCV; // For mode 1, 2
}

interface NormalizedPatientIdentity {
  numSecu: string;
  dateNaissance: string; // FR format
  rangNaissance: number;
  codeRegime: string;
  caisseGest: string;
  centreGest: string;
  nom?: string;
  prenom?: string;
  qualite?: number;
}

interface FseModeConfig {
  modeSecur: string;
  initialPieceJustifAMO: dao.PieceJustifAMO; // Default pieceJustifAMO
  adrPieceJustifCode: dao.PieceJustifAMO; // pieceJustifAMO change if ADRi
  shouldAutoCallADRi: boolean;
}

const MODE_CONFIGS: Record<number, FseModeConfig> = {
  [constants.MODE_FSE_CV]: {
    // CV
    modeSecur: 'SESAM_VITALE',
    initialPieceJustifAMO: dao.PieceJustifAMO.CARTE_VITALE,
    adrPieceJustifCode: dao.PieceJustifAMO.ADR,
    shouldAutoCallADRi: false, // Handled dynamically based on modeAppelAdri
  },
  [constants.MODE_FSE_APCV_QR]: {
    // ApCV QR
    modeSecur: 'SESAM_VITALE',
    initialPieceJustifAMO: dao.PieceJustifAMO.AUCUNE,
    adrPieceJustifCode: dao.PieceJustifAMO.APCV,
    shouldAutoCallADRi: true,
  },
  [constants.MODE_FSE_APCV_NFC]: {
    // ApCV NFC
    modeSecur: 'SESAM_VITALE',
    initialPieceJustifAMO: dao.PieceJustifAMO.AUCUNE,
    adrPieceJustifCode: dao.PieceJustifAMO.APCV,
    shouldAutoCallADRi: true,
  },
  [constants.MODE_FSE_DEGRADE]: {
    // DEGRADE
    modeSecur: 'DEGRADE',
    initialPieceJustifAMO: dao.PieceJustifAMO.AUCUNE,
    adrPieceJustifCode: dao.PieceJustifAMO.ADR,
    shouldAutoCallADRi: false,
  },
};

const formatter = new Intl.DateTimeFormat('fr-FR'); // Create a formatter for the French locale

@Component({
  selector: 'lib-bs-dialog',
  imports: [BeneficiaireComponent, CommonModule, MatRadioModule, MatCheckboxModule, ReactiveFormsModule, MatMenuModule, MatExpansionModule, MatBadgeModule, MatFormFieldModule, MatInputModule, FormsModule, MatButtonModule, MatButtonToggleModule, MatTooltipModule, MatDialogContent, MatDialogActions, RouterModule, MatDividerModule, MatIconModule],
  templateUrl: './bs-dialog.component.html',
  styleUrls: ['./bs-dialog.component.scss'],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'fr-FR' }, provideMomentDateAdapter(constants.DateFormatDatePicker)],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BsDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<BsDialogComponent>);
  readonly dialogdata: DialogData = inject<DialogData>(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  public eventtyp: number = constants.EVENTTYP_INSERT; // Insert event by default
  public fseOrder!: dao.FSEOrder; // Current FSE Order
  public factureFR!: dao.FactureFR; // DTO for invoice header
  public PieceJustifAMO = dao.PieceJustifAMO; // Expose enum to template

  public bsDTO: dao.BeneficiaireFact = dao.BeneficiaireFact.createDefault(); // beneficiaire DTO for "situation spécifique"
  public dbbsDTO: dao.BeneficiaireFact = dao.BeneficiaireFact.createDefault(); // beneficiaire DTO for DB stored benef.
  public previousPrescribers: string[] = []; // List of previously used prescribers for this patient
  public empreinteCVInserted: dao.EmpreinteCV | undefined; // Empreinte from inserted card

  public standaloneMode: boolean = true; // In standalone mode, component is in charge of reading CV card. Otherwise it is called w/ dialogdata containing a dao.EmpreinteCV
  public empreinteCVDB: dao.EmpreinteCV | undefined; // Empreinte from DB
  public empreinteADR: dao.EmpreinteAdr | undefined; // Empreinte from ADRi reading
  public activeRecipientIndex: number | undefined = 0; // Selected recipient index (in order of CV). Defaults to 0 (Assuré)
  public qualities: dao.C_value[] = [];
  public map_qualities = new Map(); // Qualités organised into a map for fast access
  public listeNirFictifs?: dao.ListeNirFictifs; // Result of RecupererListeNirFictifs call
  public adrfg!: FormGroup; // Form Group for adr search
  public errormsg: string | undefined = undefined;
  public infomsg: string | undefined = undefined;
  public infomsgHighlighted = false; // Just highlight the info message
  public loadingmsg: string | undefined = undefined;
  public adrstate: number = 0; // adr search state - 0: Init; 1: running; 2: Server Error, 3: Query param error
  //public pieceJustifAMOs!: dao.C_value[];													// C_VALUES id 1001
  //public pieceJustifAMCs!: dao.C_value[];													// C_VALUES id 1002
  public listeLibellesTaux: dao.LibelleTaux[] = [];

  public topTpAMO: boolean = true; // Checkbox must map w/ booleans => sanitizing to numbers
  public topFactAMO: boolean = true;
  public topTpAMC: boolean = false;
  public topFactAMC: boolean = false;
  public codeALD: boolean = false;

  public formattedDate!: string;
  public utils = utils; // Export to html view
  public isRecipientSaved: boolean = false; // Only for patient creation
  public processingResult!: utils.ProcessingResult; // Last process result

  public situationNature$: Observable<dao.SituationNatures> = this.stateService.situationNature$;
  private situationNatureCVSubscription?: Subscription;
  private situationNatureADRSubscription?: Subscription;
  public sns!: dao.SituationNatures;

  private today = new Date(); // Get the current date
  public msgAME: string = ''; // Just to print "AME detectée"
  public dao = dao;
  public isDisabled: boolean = false; // Is the FSE readonly?
  public isLocation: boolean = false; // True when called from a LOCATION order context
  public modeAppelAdri?: string; // A, C, M
  public previousModeAppelAdri?: string;
  public currentModeConfig!: FseModeConfig;
  public currentPatientIdentity?: NormalizedPatientIdentity;

  constructor(
    public fse: MercalinkFselibService,
    public patientService: MercalinkAPIService,
    public stateService: StateService,
    private fb: FormBuilder,
    private apiclientService: apiclient.ApiclientService,
    private valuesService: ValuesService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    console.log('[bs-dialog] ngOnInit, input dialogdata:\n', JSON.stringify(this.dialogdata, null, 2));
    this.isLocation = !!this.dialogdata?.isLocation;
    this.loadingmsg = 'Chargement';
    this.formattedDate = formatter.format(this.today); // Format the date in french form
    this.modeAppelAdri = this.dialogdata.configarea.mode_appel_adri.trim(); // Set mode_appel_adri
    this.previousModeAppelAdri = this.modeAppelAdri;
    this.currentModeConfig = MODE_CONFIGS[this.dialogdata.outputDialogData.mode] || MODE_CONFIGS[constants.MODE_FSE_DEGRADE];
    this.normalizePatientData(); // Set currentPatientIdentity
    console.log('[ngOnInit] currentModeConfig: ', JSON.stringify(this.currentModeConfig, null, 2));

    dao.qualites$.subscribe((data: any) => {
      // Load qualités into map_qualities
      this.qualities.push(data);
      this.map_qualities.set(data.id, data.name);
    });

    if (this.dialogdata?.outputDialogData?.mode === constants.MODE_FSE_DEGRADE) {
      // No CV from caller => Mode dégradé FSE from scratch or with partner data
      this.empreinteCVInserted = undefined;
      let infomsg = 'Données de carte vitale indisponibles';
      if (this.modeAppelAdri != 'M') {
        // Mode A or C => user is forced to click on ADRi query
        infomsg += ' - Lancer une Lecture ADRi ou ApCV';
      }
      this.showInfoMsg(infomsg);
      const initData = this.dialogdata.initialPatientData;
      this.adrfg = this.fb.group({
        // init ADRi search data w/ initialPatientData if provided
        numSecu: new FormControl<string>(initData?.nir || ''),
        dateNaissance: new FormControl<string>(initData?.dateNaissance || ''),
        rangNaissance: new FormControl<string>('' + (initData?.rangNaissance ?? '1')),
        codeRegime: new FormControl<string>(initData?.codeRegime || '01'),
        caisseGest: new FormControl<string>(initData?.caisseGest || ''),
        centreGest: new FormControl<string>(initData?.centreGest || ''),
      });
    } else if (this.dialogdata?.outputDialogData?.mode === constants.MODE_FSE_CV) {
      // Nominal case (CV inserted), caller (recipient or fse selector) dialog has an empreinteCV
      this.empreinteCVInserted = this.dialogdata.outputDialogData.empreinteCV; // empreinteCVInserted is set from caller																														// Nominal case. New FSE w/ empreinteCV
      this.activeRecipientIndex = this.dialogdata.outputDialogData.index; // Index is forced. Can NOT be changed afterward
      this.loadADRiForm(); // Just fills the ADRi form data
      this.situationNatureCVSubscription = this.situationNature$.pipe(skip(1)).subscribe((sns: dao.SituationNatures) => {
        // Subscribe to situationNature$ changes AND skip first inconsistent value
        this.onSituationNatureChange(sns, constants.TYP_PJ_AMO_CV); // Call onSituationNatureChange with CV SituationNature
      });
    } else if (this.dialogdata?.outputDialogData?.mode === constants.MODE_FSE_APCV_QR || this.dialogdata?.outputDialogData?.mode === constants.MODE_FSE_APCV_NFC) {
      // ApCV QR Code or NFC
      this.loadADRiForm(); // Load ADRi form from contexteAuthentificationApCV
    }

    let factureFR$: Observable<dao.FactureFR>;
    if (this.dialogdata.fse_id) {
      // Existing FSE
      factureFR$ = this.loadFse$(this.dialogdata.fse_id);
    } else {
      // New FSE
      factureFR$ = this.newFse();
    }
    factureFR$
      .pipe(
        tap((_factureFR: dao.FactureFR) => {
          console.log('[ngOnInit] factureFR created & loaded: ', JSON.stringify(_factureFR, null, 2));
        }),
        switchMap((_factureFR: dao.FactureFR) => {
          // Load patient from DB, use data from inserted CV TODO test !empreinteCVInserted, do not run on existing fse_order
          const nir = this.currentPatientIdentity?.numSecu ?? '';
          const datnaiss = this.currentPatientIdentity?.dateNaissance ? utils.tofseformat(this.currentPatientIdentity.dateNaissance) : '';
          const rang = this.currentPatientIdentity?.rangNaissance ?? 0;
          if (!nir || !datnaiss) {
            return of([] as dao.Patient[]);
          }
          return this.patientService.getRecipient$(this.dialogdata.server_url, nir, datnaiss, rang);
        }),
      )
      .subscribe({
        next: (patient: dao.Patient[]) => {
          // Patient exists => assign this.dbbsDTO
          console.log('[ngOnInit] fseorder: ', JSON.stringify(this.fseOrder, null, 2));
          try {
            if (patient.length && patient[0]['datacv']) {
              // Patient is consistent => Load dbbsDTO
              this.dbbsDTO = new dao.BeneficiaireFact(patient[0].datacv); // Load dbbsDTO w/ DB values
              //console.log("[ngOnInit] dbbsDTO loaded from DB: ", JSON.stringify(this.dbbsDTO, null, 2));
            } else {
              // No datacv, stick w/ default values from interface
            }

            // Fetch previous prescribers for this patient
            const currentNir = this.currentPatientIdentity?.numSecu ?? this.bsDTO?.numSecu ?? '';
            const currentDateNaiss = this.currentPatientIdentity?.dateNaissance ? utils.tofseformat(this.currentPatientIdentity.dateNaissance) : this.bsDTO?.dateNaissBenef ? utils.tofseformat(this.bsDTO.dateNaissBenef) : '';
            const currentRang = this.currentPatientIdentity?.rangNaissance ?? this.bsDTO?.rangNaiss ?? 1;
            const qualite = this.currentPatientIdentity?.qualite ?? this.bsDTO?.qualite ?? 0;

            if (currentNir && currentDateNaiss) {
              const patientId = `${currentNir}-${qualite}-${currentDateNaiss}-${currentRang}`;
              this.patientService.getPatientPrescribers$(this.dialogdata.server_url, patientId).subscribe({
                next: res => {
                  if (res?.data && Array.isArray(res.data)) {
                    this.previousPrescribers = res.data;
                    console.log('[ngOnInit] Loaded previous prescribers for patient:', this.previousPrescribers);
                  }
                },
                error: err => console.error('[getPatientPrescribers$] Error loading prescribers:', err),
              });
            }

            if (this.dialogdata.attachMode) {
              this.openAttachDialog();
            }
            this.loadingmsg = undefined;
          } catch (error) {
            console.error('Error while processing bsDTO:', error);
            this.loadingmsg = undefined;
            this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
            alert('Erreur fatale, la FSE est incohérente, annulez la saisie');
            // Optionally, you can call the error handler directly: this.error(error);
          }
        },
        error: error => {
          console.error('Request failed', error);
          this.loadingmsg = undefined;
          this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
        },
      });
  }

  loadFse$(fse_id: number): Observable<dao.FactureFR> {
    // Creates or loads a dao.FSEOrder from database
    return this.apiclientService.getFse$(this.dialogdata.server_url, fse_id).pipe(
      catchError(err => {
        console.error('[this.apiclientService.getFse$] error: ' + err);
        throw new Error('[this.apiclientService.getFse$] error: ' + err);
      }),
      switchMap((fseorders: dao.FSEOrder[]) => {
        if (Array.isArray(fseorders) && fseorders.length == 0) {
          // No fse in DB => Error
          console.error('Erreur FSE inexistante: '); // TODO add fse id
          throw new Error('Erreur FSE inexistante: ');
        } else {
          // FSE exists => load it
          this.eventtyp = constants.EVENTTYP_UPDATE; // Update mode. Will issue an update event
          this.initFse(fseorders[0]); // Build this.factureFR from dao.FSEOrder
          if (this.fseOrder.etat_facture === 'MODELE_LOCATION') {
            this.isLocation = true;
          }
          this.isDisabled = this.fseOrder.etat_facture != 'NOUVELLE FSE' && this.fseOrder.etat_facture != 'TARIFICATION' && this.fseOrder.etat_facture != 'MODELE_LOCATION';
          if (fseorders[0].data.processingResult) {
            this.processingResult = new utils.ProcessingResult(fseorders[0].data.processingResult.warnings, fseorders[0].data.processingResult.errors, fseorders[0].data.processingResult.fatal, fseorders[0].data.processingResult.info, fseorders[0].data.processingResult.areaResponse);
          }
          this.onDatePrescriptionChange(this.factureFR.prescription.datePresc); // Trigger specificCasesDetection
          return of(this.factureFR);
        }
      }),
    );
  }

  newFse(): Observable<dao.FactureFR> {
    console.log('[newFse] start');
    return this.buildNewFactureFR().pipe(
      switchMap(() => {
        return this.save$().pipe(
          // Early saving to get a numFacture => fseOrder
          tap((res: any) => {
            if (res?.error_code) {
              alert('Erreur à la sauvegarde : ' + JSON.stringify(res, null, 2));
              throw new Error('[this.apiclientService.getFse$] error: ' + JSON.stringify(res, null, 2));
            }
          }),
          map((res: any) => {
            // Standard case, everything is okay
            this.fseOrder.id = +res.data.id; // fse_order.id (sequence) returned by server
            this.fseOrder.id_order = this.dialogdata.order_id;
            this.factureFR.numFacture = res.data.id.toString().padStart(9, '0'); // numFacture is based on id
            console.log('[newFse] saved fseorder: ', JSON.stringify(this.fseOrder, null, 2));
            return this.factureFR; // Returns global variable built by this.newFse();
          }),
        );
      }),
    );
  }

  buildNewFactureFR(): Observable<dao.FactureFR> {
    // Build FactureFR (ie global variable - quite an anti-pattern!)
    //console.log("[buildNewFactureFR] start");
    this.factureFR = dao.FactureFR.createDefault();

    this.factureFR.dateFacture = this.formattedDate; // Init to today (french form)
    this.factureFR.dateRefAMC = (this.factureFR?.prescription?.datePresc && utils.isValidDate(this.factureFR.prescription.datePresc))
      ? this.factureFR.prescription.datePresc
      : this.factureFR.dateFacture; // dateRefAMC defaults to datePresc if valid, else dateFacture

    this.factureFR.prescription.numIdentPresc = '';
    this.factureFR.prescription.datePresc = '';
    this.factureFR.prescription.codeSpecPresc = '';
    this.factureFR.prescription.RPPSPresc = '';
    this.factureFR.prescription.numStructPresc = '';
    this.factureFR.prescription.typePresc = '';
    this.factureFR.prescription.topPrescSNCF = '0';
    this.factureFR.prescription.originePrescription = '';

    this.fseOrder = new dao.FSEOrder(0, 0, undefined, 'NOUVELLE FSE', '', this.factureFR, this.empreinteCVInserted, 0, undefined, undefined, undefined, [], []);
    this.factureFR.modeSecur = this.isLocation ? 'SESAM_SANS_VITALE' : this.currentModeConfig.modeSecur; // Rental => only SESAM_SANS_VITALE

    if (this.dialogdata?.outputDialogData?.mode === constants.MODE_FSE_CV) {
      // CV inserted => Init bsDTO from CV, run onConsulterADR$() if modeAppelAdri=='A'
      const beneficiaire = this.empreinteCVInserted?.beneficiaire[+this.activeRecipientIndex!];
      const refDate = this.getReferenceDate();

      // 1. Call the SILENT version (Synchronous)
      const sns: dao.SituationNatures = this.stateService.getCalculatedSns(beneficiaire, this.empreinteCVInserted?.codeRegime, this.empreinteCVInserted?.codeGest, refDate, constants.TYP_PJ_AMO_CV);
      // 2. Manually trigger the DTO update immediately
      this.onSituationNatureChange(sns, constants.TYP_PJ_AMO_CV);

      // 3. (Optional) Start ADRi in the background if needed
      if (this.isCallADR(sns)) {
        this.onLectureADRClick(this.getReferenceDateString());
      }

      // 4. Return an immediate Observable. save$() will now see a completed bsDTO.
      return of(this.factureFR);
    } else if (this.dialogdata.outputDialogData.mode == constants.MODE_FSE_DEGRADE) {
      // Mode CV but no CV => no pièces justificatives, mode dégradé
      this.topFactAMC = false;
      this.topTpAMC = false;
      // Mode dégradé (sans carte vitale) : si le client sélectionné en amont (c_partner)
      // possède déjà des données de facturation Sécu résolues (soit via fse_patient [Cas 1A],
      // soit via les données de reprise orthop/cufs [Cas 1B]), initialPatientData est renseigné.
      // On pré-remplit directement le DTO de facturation (bsDTO : NIR, nom, prénom, date/rang de naissance,
      // régime, caisse, centre AMO) afin d'initialiser le dossier sans exiger de ressaisie manuelle.
      if (this.dialogdata.initialPatientData) {
        const init = this.dialogdata.initialPatientData;
        if (init.nom) this.bsDTO.nom = init.nom;
        if (init.prenom) this.bsDTO.prenom = init.prenom;
        if (init.nir) this.bsDTO.numSecu = init.nir;
        if (init.dateNaissance) this.bsDTO.dateNaissBenef = init.dateNaissance;
        if (init.rangNaissance !== undefined) this.bsDTO.rangNaiss = init.rangNaissance;
        if (init.qualite !== undefined) this.bsDTO.qualite = init.qualite;
        if (init.codeRegime) this.bsDTO.attestationAMO.regime = init.codeRegime;
        if (init.caisseGest) this.bsDTO.attestationAMO.caisseGest = init.caisseGest;
        if (init.centreGest) this.bsDTO.attestationAMO.centreGest = init.centreGest;
      }
      if (this.isLocation || this.modeAppelAdri == 'A' || this.modeAppelAdri == 'C') {
        // Location ou Mode auto ou C : proposer ADRi par défaut
        this.bsDTO.attestationAMO.pieceJustifAMO = dao.PieceJustifAMO.ADR;
        this.bsDTO.attestationAMC.pieceJustifAMC = '0';
      } else {
        // Mode manuel
        this.bsDTO.attestationAMO.pieceJustifAMO = this.currentModeConfig.initialPieceJustifAMO;
        this.bsDTO.attestationAMC.pieceJustifAMC = '0';
      }
      return of(this.factureFR);
    } else if (this.currentModeConfig.shouldAutoCallADRi) {
      // Get recipient from ApCV token
      this.onLectureADRClick(this.getReferenceDateString());
      return of(this.factureFR);
    }
    return of(this.factureFR);
  }

  initFse(fseOrder: dao.FSEOrder) {
    // FSE exists => load it ie build this.factureFR from database data
    // Need some formatting: DB data => human lisible format
    //factureFR.dateFacture = formatter.format(utils.getDateFromYYYYMMDD(factureFR.dateFacture));	// Get format back to french
    //factureFR.dateRefAMC = formatter.format(utils.getDateFromYYYYMMDD(factureFR.dateRefAMC));	// Get format back to french
    this.fseOrder = fseOrder;
    this.factureFR = JSON.parse(JSON.stringify(fseOrder.data.factureFR));
    if (this.isLocation) {
      // Rental => only SESAM_SANS_VITALE
      this.factureFR.modeSecur = 'SESAM_SANS_VITALE';
    }
    this.empreinteCVInserted = fseOrder.data.empreinteCV; // TODO use mode to load consistenly empreinteCVInserted OR empreinteADR
    this.empreinteADR = fseOrder.data.empreinteADR;
    this.bsDTO = this.factureFR.beneficiaire; // TODO check that. CV data may be lost in the process
    //this.bsDTO.dateNaissBenef = utils.getDateStringFromYYYYMMDD(this.bsDTO.dateNaissBenef)??'';								// From fse to  french
    this.fromFactureFR(this.factureFR); // Set local variables
    console.log('[initFse] this.fseOrder: ', this.fseOrder);
    console.log('[initFse] this.bsDTO: ', this.bsDTO);
  }

  onLectureADRClick(referenceDate_s?: string) {
    // User clicked on "Lecture ADRi". WARNING Can be a "forced" click by mode CIBLÉ OR ApCV
    //	topActionPS depends on modeAppelAdri and calling mode
    const topActionPS = ['A', 'C'].includes(this.modeAppelAdri ?? '') || [constants.MODE_FSE_APCV_QR, constants.MODE_FSE_APCV_NFC].includes(this.dialogdata.outputDialogData.mode) ? '0' : '1';
    // referenceDate is taken from param OR prescription (called from GUI)
    const refDateStr = this.getReferenceDateString(referenceDate_s);
    const referenceDate: Date = this.getReferenceDate(referenceDate_s);
    const mode = this.modeAppelAdri == 'A' ? 'AUTO' : this.modeAppelAdri == 'C' ? 'CIBLÉ' : 'MANUEL';
    console.log(`[onLectureADRClick] referenceDate: ${referenceDate}`);
    this.onConsulterADR$(topActionPS, refDateStr)
      .pipe(take(1))
      .subscribe({
        next: (res?: dao.ConsulterADRResponse) => {
          if (res?.HTTPResponse.empreinteADR) {
            // Update bsDTO w/ values from ARDi response
            this.processADRiResponse(res.HTTPResponse.empreinteADR, referenceDate, mode);
          } else {
            this.showError('Erreur inattendue à la lecture ADRi');
          }
        },
        error: err => console.error('ADRi error handled in stream', err),
      });
  }

  private processADRiResponse(empreinteADR: dao.EmpreinteAdr, referenceDate: Date, mode: string) {
    this.bsDTO.attestationAMO.pieceJustifAMO = this.currentModeConfig.adrPieceJustifCode;
    this.bsDTO.attestationAMC.pieceJustifAMC = 'A'; // Force ADRi(?)
    console.log(`[processADRiResponse] modeSecur: ${this.currentModeConfig.modeSecur}`);
    console.log('[processADRiResponse] empreinteADR:\r\n', JSON.stringify(empreinteADR, null, 2));

    // Unsubscribe for all - Register situationNature$ for ADRi
    if (this.situationNatureCVSubscription) {
      this.situationNatureCVSubscription.unsubscribe();
    }
    if (this.situationNatureADRSubscription) {
      this.situationNatureADRSubscription.unsubscribe();
    }
    this.situationNatureADRSubscription = this.situationNature$.pipe(skip(1)).subscribe((sns: dao.SituationNatures) => {
      // Subscribe to situationNature$ changes. Skip first BehaviorSubject
      // this.onSituationNatureChange(sns, constants.TYP_PJ_AMO_ADR);			// FIXME																	// Call onSituationNatureChange with ADRi SituationNature
    });
    // Run specificCasesDetection for ADR. Will trigger onSituationNatureChange
    //if (referenceDate) this.stateService.specificCasesDetection(empreinteADR.beneficiaire, this.empreinteCVInserted?.codeRegime, this.empreinteCVInserted?.codeGest, referenceDate, constants.TYP_PJ_AMO_ADR); // Emit situationNatures
    this.sns = this.stateService.getCalculatedSns(empreinteADR.beneficiaire, this.empreinteCVInserted?.codeRegime, this.empreinteCVInserted?.codeGest, referenceDate, constants.TYP_PJ_AMO_ADR);
    this.onSituationNatureChange(this.sns, constants.TYP_PJ_AMO_ADR); // FIXME nexw change
    this.onPieceJustifChange(); // Rebuild bsDTO
    if (empreinteADR.beneficiaire?.donneesMutuelle?.mutnum == '75500025') {
      // Test 5.16. Change 75500025 to 75500017 on the fly, set TP AMC
      empreinteADR.beneficiaire.donneesMutuelle.mutnum = '75500017';
    }
    console.log('[onLectureADRClick] changed bsDTO (from ADRi):\r\n', JSON.stringify(this.bsDTO, null, 2));
    if ([constants.MODE_FSE_APCV_QR, constants.MODE_FSE_APCV_NFC].includes(this.dialogdata.outputDialogData.mode)) {
      this.showInfoMsg('Données ADRi chargées depuis ApCV');
    } else {
      this.showInfoMsg(`Données ADRi chargées en mode ${mode}`);
    }
  }

  // Run fse consulterADR$ w/ some controls
  // dateConsultation is in FR format
  onConsulterADR$(topActionPS: string, dateConsultation?: string): Observable<dao.ConsulterADRResponse | undefined> {
    // <criteresADR><numSecu>140016322075508</numSecu> <dateNaissBenef>19400101</dateNaissBenef> <rangNaiss>1</rangNaiss> <regime>01</regime></criteresADR>

    if (!dateConsultation) {
      const msg = 'La date de référence est obligatoire';
      this.showError(msg);
      this.adrstate = 3;
      return throwError(() => new Error(msg));
    }

    const date_s = utils.tofseformat(this.adrfg.get('dateNaissance')?.value);
    if (!date_s) {
      const msg = 'Format de date invalide: ' + this.adrfg.get('dateNaissance')?.value;
      this.showError(msg);
      this.adrstate = 3;
      return throwError(() => new Error(msg));
    }

    if (!this.adrfg.get('codeRegime')?.value) {
      const msg = 'Le régime est obligatoire';
      this.showError(msg);
      this.adrstate = 3;
      return throwError(() => new Error(msg));
    }

    this.adrstate = 1; // State = Running

    const rangNaissance = this.adrfg.get('rangNaissance')?.value;
    const codeRegime = this.adrfg.get('codeRegime')?.value;
    const caisseGest = this.adrfg.get('caisseGest')?.value;
    const centreGest = this.adrfg.get('centreGest')?.value;

    // Mandatory params
    let critereADRxmldata = `<criteresADR>
        <numSecu>${this.adrfg.get('numSecu')?.value}</numSecu>
        <dateNaissBenef>${date_s}</dateNaissBenef>
        <rangNaiss>${rangNaissance}</rangNaiss>
        <regime>${codeRegime}</regime> `;
    // Optionnal params
    if (caisseGest) critereADRxmldata += `<caisseGest>${caisseGest}</caisseGest>`;
    if (centreGest) critereADRxmldata += `<centreGest>${centreGest}</centreGest>`;

    critereADRxmldata += '</criteresADR>';

    const dateConsultationFSE = utils.tofseformat(dateConsultation); // Convert FR format to fse
    const numIdentFact = this.dialogdata.outputDialogData.numIdentFact;
    const nomLogiciel = this.dialogdata.configarea.nomLogiciel;
    const versionCommerciale = this.dialogdata.configarea.versionCommerciale;
    const versionADR = this.dialogdata.configarea.versionADR;
    const numAutorisation = this.dialogdata.configarea.numAutorisationAdri;

    return this.fse.consulterADR$(this.dialogdata.area_baseurl, critereADRxmldata, topActionPS, dateConsultationFSE, numIdentFact, nomLogiciel, versionCommerciale, versionADR, numAutorisation, this.dialogdata.configarea.nomRessLect).pipe(
      switchMap((res: dao.ConsulterADRResponse) => {
        if (res && res.HTTPResponse.codeRetour == '0' && res?.HTTPResponse.empreinteADR) {
          this.empreinteADR = res?.HTTPResponse.empreinteADR;
          this.adrstate = 0; // Back to adr state "init"
          return of(res);
        } else {
          // Functionnal error => show message to user
          this.empreinteADR = undefined;
          this.adrstate = 2; // State = Error
          this.showError(res.HTTPResponse.libRetour, 'ERREUR Serveur ADRi');
          return throwError(() => new Error(res.HTTPResponse.libRetour));
        }
      }),
      catchError(error => {
        // FATAL technical error
        console.error('Request failed', error);
        this.loadingmsg = undefined;
        this.errormsg = error instanceof Error ? error.message : JSON.stringify(error);
        this.adrstate = 2;
        return throwError(() => error);
      }),
    );
  }

  saveHCPatient() {
    // Save patient data from custom input zone. Trigger EVENTTYP_FSE_UPDATESPECIF if patient exists, EVENTTYP_INSERT otherwise
    this.bsDTO.qualite = +this.bsDTO.qualite;
    this.bsDTO.rangNaiss = +this.bsDTO.rangNaiss;
    this.bsDTO.attestationAMO.pieceJustifAMO = String(this.bsDTO.attestationAMO.pieceJustifAMO) as dao.PieceJustifAMO; // For some reason it sends a number(?)
    this.bsDTO.codeALD = this.codeALD ? '1' : '0';
    console.log('[saveHCPatient] this.bsDTO: ', JSON.stringify(this.bsDTO));
    this.apiclientService.postFsePatientEvent$(this.dialogdata.server_url, constants.EVENTTYP_FSE_UPDATESPECIF, this.dialogdata.userid, this.bsDTO).subscribe({
      next: (_res: any) => {
        //this.apiResponseClients = items;
        //console.log("postFsePatientEvent : ", res);
        this.dbbsDTO = new dao.BeneficiaireFact(this.bsDTO);
        this.isRecipientSaved = true;
        this.showInfoMsg('Infos patient sauvegardées');
      },
      error: e => {
        console.error('ERREUR Serveur : ', e.error);
        alert('ERREUR Serveur : ' + e.error);
        //this.spinner.hide();
        //this.alertService.error("ERREUR Serveur : " + e.error);	// TEMPLATE subscribe call
      },
    });
  }

  loadHCPatient() {
    const oldpieceJustifAMC = this.bsDTO.attestationAMC.pieceJustifAMC; // WARNING Keep previous pieceJustifAMC setting
    this.bsDTO = new dao.BeneficiaireFact(this.dbbsDTO);
    this.bsDTO.attestationAMC.pieceJustifAMC = oldpieceJustifAMC;
    this.factureFR.beneficiaire = this.bsDTO;
    this.codeALD = this.bsDTO.codeALD === '1';
    if (this.bsDTO.attestationAMO?.regime) {
      this.onBlurRegime();
    }
    if (this.bsDTO.attestationAMC?.mutnum) {
      this.onBlurMutnum();
    }
  }

  /**
   * Opens the PrescriptionComponent dialog to edit prescription data.
   * @param onSuccess Optional callback returning boolean indicating if prescription was saved.
   */
  openPrescriptionDialog(onSuccess?: (saved: boolean) => void): void {
    const dialogRef = this.dialog.open(PrescriptionComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: this.dialogdata.server_url,
        order_id: this.dialogdata.order_id,
        factureFR: this.factureFR,
        isDisabled: this.isDisabled,
        previousPrescribers: this.previousPrescribers,
      },
    });
    dialogRef.afterClosed().subscribe((result: dao.Prescription | undefined) => {
      if (!result) {
        if (onSuccess) onSuccess(false);
        return;
      }
      const oldDatePresc = this.factureFR.prescription.datePresc;
      this.factureFR.prescription = result;
      if (result.datePresc && utils.isValidDate(result.datePresc)) {
        this.factureFR.dateRefAMC = result.datePresc;
        this.syncPrestationsWithDateRefAMC();
      }
      if (result.datePresc !== oldDatePresc) {
        console.log('Prescription dialog closing result:\n', JSON.stringify(result));
        this.onDatePrescriptionChange(result.datePresc); // Trigger specificCasesDetection
      }
      if (onSuccess) onSuccess(true);
    });
  }

  onDateRefAMCChange(_newDate: string): void {
    this.syncPrestationsWithDateRefAMC();
  }

  public syncPrestationsWithDateRefAMC(): void {
    if (!this.factureFR?.dateRefAMC || !utils.isValidDate(this.factureFR.dateRefAMC)) {
      return;
    }
    const fseDate = utils.tofseformat(this.factureFR.dateRefAMC);
    if (!fseDate || !this.factureFR.prestationFR) {
      return;
    }
    for (const p of this.factureFR.prestationFR) {
      if (p.LPP?.typePrestaLPP !== 'L') {
        p.dateExec = fseDate;
        if (p.LPP) {
          p.LPP.dateDebut = fseDate;
          if (p.LPP.dateFin) {
            p.LPP.dateFin = fseDate;
          }
        }
      }
    }
  }

  openPecDialog() {
    const dialogRef = this.dialog.open(PecComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: this.dialogdata.server_url,
        order_id: this.dialogdata.order_id,
        factureFR: this.factureFR,
        isDisabled: this.isDisabled,
      },
    });
    dialogRef.afterClosed().subscribe((result: dao.PEC | undefined) => {
      if (result !== undefined) {
        console.log('PEC dialog closing result:\n', JSON.stringify(result));
        if (result.montantPECAVentiler != 0 && result.refPEC) {
          this.factureFR.PEC = result;
        }
      }
    });
  }

  openNatAssuDialog(msg: string, forceAM: boolean = false): void {
    // forceAM Force le code AM
    const dialogRef = this.dialog.open(NatAssuComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        msg: msg,
        server_url: this.dialogdata.server_url,
        order_id: this.dialogdata.order_id,
        factureFR: this.factureFR,
        empreinteCVInserted: this.empreinteCVInserted,
        empreinteADR: this.bsDTO.attestationAMC.pieceJustifAMC == 'A' ? this.empreinteADR : undefined,
        forceAM: forceAM, // Indicateur Alsace Moselle
      } as NatassuDialogData,
    });
    dialogRef.afterClosed().subscribe(
      (
        result:
          | {
              maladie: dao.Maladie;
              maternite: dao.Maternite;
              accidentTravail: dao.AccidentTravail;
              smg: dao.SMG;
            }
          | undefined,
      ) => {
        if (result !== undefined) {
          // Closed on "Save" Complete factureFR fields
          console.log('NatAssuComponent dialog closing result:\n', JSON.stringify(result));
          switch (
            this.factureFR.natureAssurance // Set maladie, maternite.. according to natureAssurance
          ) {
            case 'maladie':
              this.factureFR.maladie = result.maladie as dao.Maladie;
              break;
            case 'maternite':
              this.factureFR.maternite = result.maternite as dao.Maternite;
              break;
            case 'accidentTravail':
              this.factureFR.accidentTravail = result.accidentTravail as dao.AccidentTravail;
              console.log('this.factureFR.accidentTravail: ', this.factureFR.accidentTravail);
              break;
            case 'smg':
              this.factureFR.smg = result.smg;
              console.log('this.factureFR.smg: ', this.factureFR.smg);
              break;
          }
        }
      },
    );
  }

  openRecipientDialog(index: number) {
    // Zoom on recipient
    const dialogRef = this.dialog.open(RecipientComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        beneficiaire: this.empreinteCVInserted?.beneficiaire[index], // TODO what if this.empreinteCVInserted is undefined? (existing FSE)
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('The recipîent dialog was closed');
    });
  }

  openAttachDialog() {
    const dialogRef = this.dialog.open(AttachComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: this.dialogdata.server_url,
        area_baseurl: this.dialogdata.area_baseurl,
        fse_id: this.fseOrder.id,
        numIdentFact: this.dialogdata.outputDialogData.numIdentFact,
        factureFR: this.factureFR,
        etat_facture: this.fseOrder.etat_facture,
      },
    });
    dialogRef.afterClosed().subscribe(_result => {
      console.log('The attachement dialog was closed');
    });
  }

  openTarificationDialog() {
    if (!this.isPrescriptionComplete) {
      this.openPrescriptionDialog((saved: boolean) => {
        if (saved && this.isPrescriptionComplete) {
          this.openTarificationDialog();
        }
      });
      return;
    }
    this.facturer$(this.fseOrder.id, this.fseOrder.id_order, true, false).subscribe({
      // Mode simulation pour compléter la page prestations
      next: () => {
        // Lance une simulation => montants de remb.
        console.log('[openTarificationDialog] factureFR: ', JSON.stringify(this.factureFR, null, 2));
        if (this.processingResult.isError()) {
          // facturer$ (simulation) did NOT went well => stop processing
          alert('Erreur rencontrée dans le processus de facturation. Consulter les messages');
          return; // Stop on error
        }
        //console.log("[openTarificationDialog] processingResult: ", JSON.stringify(this.processingResult, null, 2));
        const dialogRef = this.dialog.open(PrestationsComponent, {
          height: 'unset',
          maxWidth: 'unset',
          panelClass: 'dialog-container-wide',
          disableClose: true,
          data: {
            factureFR: this.factureFR,
            codeALD: this.codeALD,
            isLocation: this.isLocation,
          },
        });
        dialogRef.afterClosed().subscribe({
          next: (result: PrestationsDialogResult) => {
            if (!result) {
              console.error('SHOULD NOT HAPPEN The prestations dialog was closed on cancel');
              return; // Dialog exited on cancel
            }
            this.factureFR.prestationFR = result.prestations; // Dialog result (prestations) is persisted in factureFR
            if (this.isLocation && result.validated) {
              this.fseOrder.etat_facture = 'MODELE_LOCATION';
              this.save$().subscribe({
                next: (_res: any) => {
                  this.dialogRef.close({
                    station: this.dialogdata.station,
                    factureFR: this.factureFR,
                    processingResult: this.processingResult,
                    fse_id: this.fseOrder?.id,
                    etat_facture: 'MODELE_LOCATION',
                  });
                },
                error: (error: any) => {
                  console.error('Erreur sauvegarde modèle FSE location:', error);
                  alert('Erreur lors de la sauvegarde du modèle FSE : ' + (error?.message || JSON.stringify(error)));
                },
              });
            } else if (result.facturer && !this.isLocation) {
              // Dialog was closed asking for real facturation
              this.facturer$(this.fseOrder.id, this.fseOrder.id_order, false, false).subscribe({
                // Facturation Mode Réel
                next: (_res: any) => {
                  //console.log("[facturer] postFseOrderEvent$ result: ", JSON.stringify(res, null, 2));
                  //console.log("Control processingResult: ", this.processingResult);
                  /*									if (this.processingResult.isError()) {	// facturer$ did NOT went well => stop processing
										alert("Erreur rencontrée dans le processus de facturation. Consulter les messages");
										return;	// Stop on error
									}*/
                },
                error: (error: any) => {
                  if (error instanceof TypeError) {
                    alert('ERREUR : ' + error.message);
                  } else if (typeof error === 'object') {
                    alert('ERREUR Serveur facturation : ' + JSON.stringify(error, null, 4));
                  } else {
                    alert('ERREUR Serveur facturation : ' + error);
                  }
                },
              });
            }
          },
        });
      },
      error: (error: any) => {
        if (error instanceof TypeError) {
          alert('ERREUR : ' + error.message);
        } else if (typeof error === 'object') {
          alert('ERREUR Serveur facturation : ' + JSON.stringify(error, null, 4));
        } else {
          alert('ERREUR Serveur facturation : ' + error);
        }
      },
    });
  }

  facturer() {
    if (this.isLocation) return;
    if (!this.isPrescriptionComplete) {
      this.openPrescriptionDialog((saved: boolean) => {
        if (saved && this.isPrescriptionComplete) {
          this.facturer();
        }
      });
      return;
    }
    this.facturer$(this.fseOrder.id, this.fseOrder.id_order, false, false).subscribe({
      // Facturation Mode Réel
      next: (_res: any) => {},
      error: (error: any) => {
        if (error instanceof TypeError) {
          alert('ERREUR : ' + error.message);
        } else if (typeof error === 'object') {
          alert('ERREUR Serveur facturation : ' + JSON.stringify(error, null, 4));
        } else {
          alert('ERREUR Serveur facturation : ' + error);
        }
      },
    });
  }

  private detruireContexteApCV() {
    // Only destroy context if it was an ApCV mode
    if (this.dialogdata.outputDialogData.mode === constants.MODE_FSE_APCV_QR || this.dialogdata.outputDialogData.mode === constants.MODE_FSE_APCV_NFC) {
      const config = this.dialogdata.configarea;
      this.fse
        .detruireContexteAuthentificationApCV$(this.dialogdata.area_baseurl, this.dialogdata.outputDialogData.numIdentFact, config.nomRessLect, config.slotCPS)
        .pipe(take(1)) // Ensure it completes
        .subscribe({
          next: (res: any) => {
            if (+res.HTTPResponse.codeRetour < 0) {
              console.error('detruireContexteAuthentificationApCV call error', res.HTTPResponse.libRetour);
              // Do not show error to user, it is not critical here.
            } else {
              console.log('ApCV Context deleted after facturation:', JSON.stringify(res, null, 2));
            }
          },
          error: (err: any) => {
            console.error('ApCV context destruction error handled in stream', err);
            // Do not show error to user.
          },
        });
    }
  }

  openInfoDialog(change: boolean) {
    // Dialog info for ADRi modes
    const dialogRef = this.dialog.open(InfoadriComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        change: change,
      },
    });
    dialogRef.afterClosed().subscribe(_result => {
      console.log('The infoadri dialog was closed');
    });
  }

  openCPSDialog() {
    // Dialog for CPS situations
    const dialogRef = this.dialog.open(CpsComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: this.dialogdata.server_url,
        area_baseurl: this.dialogdata.area_baseurl,
        userid: this.dialogdata.userid,
        numIdentFact: this.dialogdata.outputDialogData.numIdentFact,
      },
    });
    dialogRef.afterClosed().subscribe(_result => {
      console.log('The CpsComponent dialog was closed');
    });
  }

  // Close page => save OR cancel
  onClose(save: boolean): void {
    try {
      if (save) {
        this.save$().subscribe({
          // Post save event
          next: (res: any) => {
            this.showInfoMsg(res.msg);
          },
          error: e => {
            console.error('ERREUR Serveur : ', e.error);
            alert('ERREUR Serveur : ' + e.error);
          },
        });
      } else {
        this.dialogRef.close({ station: this.dialogdata.station, factureFR: this.factureFR, processingResult: this.processingResult, fse_id: this.fseOrder?.id, etat_facture: this.fseOrder?.etat_facture });
      }
    } catch (error) {
      this.errormsg = 'Erreur sauvegarde: ' + error;
    }
  }

  public save$(): Observable<any> {
    this.syncPrestationsWithDateRefAMC();
    this.bsDTO.codeALD = this.codeALD ? '1' : '0';
    console.log('[save$]');
    //console.log("[save$] bsDTO" + JSON.stringify(this.bsDTO, null, 2));
    this.factureFR.beneficiaire = this.bsDTO; // Specific data => factureFR
    //console.log("[save$] input factureFR:\n" + JSON.stringify(this.factureFR, null, 2));
    const data: any = {
      mode: this.dialogdata.outputDialogData.mode,
      order_id: this.dialogdata.order_id, // Just usefull for retrieving order on server side
      id: this.fseOrder.id, // Same here
      factureFR: this.toFactureFR(this.factureFR), // Format factureFR to be compliant w/ FSE
      empreinteCV: this.empreinteCVInserted,
      contexteAuthentificationApCV: this.dialogdata.outputDialogData.contexteAuthentificationApCV,
      empreinteADR: this.empreinteADR,
      index: this.dialogdata.outputDialogData.index,
      state: this.fseOrder.etat_facture,
    };
    if (this.processingResult) {
      data.processingResult = this.processingResult;
    }

    return this.apiclientService.postFseOrderEvent$(this.dialogdata.server_url, this.fseOrder.id, this.eventtyp, this.dialogdata.userid, data).pipe(
      catchError(error => {
        let errmsg = error;
        if (typeof error == 'object') {
          errmsg = JSON.stringify(error, null, 4);
        }
        console.error('[this.apiclientService.postFseOrderEvent$] error: ' + errmsg);
        throw new Error('[this.apiclientService.postFseOrderEvent$] error: ' + errmsg);
      }),
      tap(_ => {
        this.eventtyp = constants.EVENTTYP_UPDATE; // Update mode. Will issue an update event
      }),
    );
  }

  public facturer$(id_fse: number, id_order: number, topSimulation: boolean, topAssistance: boolean = false): Observable<any> {
    // Facturation, simulation ou Assistance STS (utile ?)
    this.syncPrestationsWithDateRefAMC();
    this.processingResult = this.controlFacture(); // Local FSE control
    if (this.processingResult.isError()) return throwError(() => new Error('Erreur au contrôle de la facture')); // Found errors => exit
    //if (!this.dialogdata.fse_id) return throwError(() => new Error('Erreur identifiant FSE non renseigné'));
    let step = 'save';
    return this.save$().pipe(
      // Save fse_order. this.fse.facturer works on saved data
      switchMap(_res => {
        //console.log("[save$] res: ", JSON.stringify(_res, null, 2));
        step = 'Facturation';
        return this.fse.facturer(this.dialogdata.area_baseurl, this.dialogdata.server_url, id_fse, id_order, this.dialogdata.userid, topSimulation, topAssistance, this.dialogdata.configarea).pipe(
          //tap((res: utils.ProcessingResult) => { console.log("[fse.facturer] res: ", JSON.stringify(res, null, 2)); }),
          switchMap((processingResult: utils.ProcessingResult) => {
            this.processingResult = processingResult;
            // Prestations have been calculated (this is the whole point)
            console.log(`%c[facturer$] return from fse.facturer:\n${JSON.stringify(processingResult, null, 2)}`, 'color: violet;');
            if (processingResult.areaResponse?.HTTPResponse.codeRetour === '0') {
              // All went well, assign prestations & convention
              if (!Array.isArray(processingResult.areaResponse.HTTPResponse.factureFR.prestationFR)) {
                // Force prestationFR to Array
                this.factureFR.prestationFR = [];
                this.factureFR.prestationFR.push(processingResult.areaResponse.HTTPResponse.factureFR.prestationFR);
              } else {
                this.factureFR.prestationFR = processingResult.areaResponse.HTTPResponse.factureFR.prestationFR;
              }

              if (this.factureFR?.beneficiaire?.attestationAMC?.idConvention != undefined && processingResult.areaResponse.HTTPResponse.factureFR?.beneficiaire?.attestationAMC?.idConvention != undefined) this.factureFR.beneficiaire.attestationAMC.idConvention = processingResult.areaResponse.HTTPResponse.factureFR.beneficiaire.attestationAMC.idConvention;

              // Change fseOrder etat_facture
              if (topSimulation || topAssistance) {
                this.fseOrder.etat_facture = 'TARIFICATION';
              } else {
                switch (
                  processingResult.areaResponse.HTTPResponse.factureFR.modeSecur // Facturation en réél
                ) {
                  case 'SESAM_VITALE':
                    this.fseOrder.etat_facture = 'SECURISÉE';
                    break;
                  case 'DESYNCHRONISE':
                    this.fseOrder.etat_facture = 'NON_SECURISÉE';
                    break;
                  case 'SESAM_SANS_VITALE':
                    this.fseOrder.etat_facture = 'SESAM_SANS_VITALE';
                    break;
                  case 'DEGRADE':
                    this.fseOrder.etat_facture = 'DEGRADÉE';
                    break;
                  default:
                    this.fseOrder.etat_facture = 'ETAT_INCONNU';
                }
                this.detruireContexteApCV(); // After invoicing, ApCV context must be destroyed
              }
            } else {
              // Error is in processingResult
              console.error('[facturer$] Error is in processingResult. Before RAZ ETM\nprocessingResult.areaResponse.HTTPResponse.factureFR', JSON.stringify(processingResult?.areaResponse?.HTTPResponse.factureFR, null, 2));
              this.factureFR.prestationFR.forEach((prestation: dao.PrestationFR, _i) => {
                // Reset user input codeJustifETM (could prevent further tarification)
                prestation.codeJustifETM = undefined; // TODO a few more cleanup to do ...
              });
              // alert(`Erreur n° ${processingResult.areaResponse?.HTTPResponse.codeRetour} rencontrée dans le processus de facturation. Consulter les messages`);
              console.error('[facturer$] Error is in processingResult. After RAZ ETM', JSON.stringify(this.factureFR.prestationFR, null, 2));
            }
            this.isDisabled = this.fseOrder.etat_facture != 'NOUVELLE FSE' && this.fseOrder.etat_facture != 'TARIFICATION' && this.fseOrder.etat_facture != 'MODELE_LOCATION';
            step = 'Posting FSE Event';
            //console.log('Switching to postFseOrderEvent');
            //console.log('[facturer$ after facturer] this.factureFR.prestationFR: ', JSON.stringify(this.factureFR.prestationFR, null, 2));
            const data: any = {
              order_id: id_order,
              id: id_fse,
              processingResult: this.processingResult,
              state: this.fseOrder.etat_facture,
            };
            // delete data.HTTPResponse['idSession']; TODO clean data before saving
            // Now save FSE w/ processingResult and state
            return this.apiclientService.postFseOrderEvent$(this.dialogdata.server_url, this.dialogdata.order_id, constants.EVENTTYP_FSE_UPDATEORDERDATA, this.dialogdata.userid, data);
          }),
        );
      }),
    );
  }

  loadADRiForm() {
    // Load ADRi query form w/ empreinteCVInserted (inserted CV data)
    if (this.currentPatientIdentity) {
      this.adrfg = this.fb.group({
        // ADRi search data init w/ CV data
        numSecu: new FormControl<string>(this.currentPatientIdentity.numSecu),
        dateNaissance: new FormControl<string>(this.currentPatientIdentity.dateNaissance),
        rangNaissance: new FormControl<string>('' + this.currentPatientIdentity.rangNaissance),
        codeRegime: new FormControl<string>(this.currentPatientIdentity.codeRegime),
        caisseGest: new FormControl<string>(this.currentPatientIdentity.caisseGest),
        centreGest: new FormControl<string>(this.currentPatientIdentity.centreGest),
      });
    }
  }

  // Code couverture changed => code situation changed => adapter la nature d'assurance
  onSituationNatureChange(sns: dao.SituationNatures, typ_pj_amo: number) {
    console.log(`[onSituationNatureChange] ${typ_pj_amo == constants.TYP_PJ_AMO_CV ? 'CV' : 'ADR'} sns: `, JSON.stringify(sns, null, 2));
    this.sns = sns;
    const sn: dao.SituationNature = sns[typ_pj_amo];
    const isSituationChanged = this.factureFR.sn?.pc?.codeSituation !== sn.pc?.codeSituation;
    this.factureFR.sn = sn;
    // Détection des natures d'assurance
    if (sn.pc) {
      // Une période couverture a changé ? => maj nature assurance
      //this.situationNature.pc = sn.pc;																	// Update local values (=> in sync w/ beneficiaire)
      //this.situationNature.nature = sn.nature;
      this.bsDTO.codeSituation = sn.pc.codeSituation; // Selon AF ignoré en entrée si PJ CV ?
      this.bsDTO.codeALD = sn.pc.codeALD; // Idem

      if (isSituationChanged) {
        if (sn.pc.codeSituation === '0103') {
          // Maternité"
          this.factureFR.natureAssurance = 'maternite';
          this.openNatAssuDialog('Situation de maternité (0103) détectée\nVeuillez saisir une date de maternité');
        } else if (sn.pc.codeSituation === '0105') {
          // Alsace-Moselle
          this.factureFR.natureAssurance = 'maladie';
          this.openNatAssuDialog('Régime Alsace Moselle (0105) détecté. Indicateur Alsace Moselle forcé', true); // Open NatAssuDialog forcing AM indicator
        } else if (sn.pc.codeSituation === '0225') {
          // Accident (potentiel) => ouvrir le dialog "maladie"
          this.factureFR.natureAssurance = 'maladie';
          this.openNatAssuDialog("Situation d'accident (0225) détectée. Veuillez saisir les informations");
        } else {
          this.factureFR.natureAssurance = 'maladie';
        }
      }
    }

    // MAJ topFactAMO / AMC en fonction des situations spécifiques
    if (sn.sp !== undefined) {
      // Modification de situation spécifique
      //this.situationNature.sp = sn.sp;
      switch (sn.sp) {
        case dao.SituationSpecifique.NONE:
          console.log('Pas de situation spécifique');
          break;

        case dao.SituationSpecifique.SP03a:
        case dao.SituationSpecifique.SP03b:
        case dao.SituationSpecifique.SP03c:
        case dao.SituationSpecifique.SP03d:
          console.log(`Bénéficiaire de la C2S (${sn.sp})`);
          setTimeout(() => {
            this.topFactAMC = true;
          }, 0); // Force Fact & TP AMC
          setTimeout(() => {
            this.topTpAMC = true;
          }, 0);
          break;

        case dao.SituationSpecifique.SP04:
          console.log('Bénéficiaire sortant de C2S');
          setTimeout(() => {
            this.topFactAMO = true;
          }, 1); // Force Fact & TP AMO (should already be the case)
          setTimeout(() => {
            this.topTpAMO = true;
          }, 1);
          break;

        case dao.SituationSpecifique.SP06:
          console.log('Bénéficiaire de l’AME');
          setTimeout(() => {
            this.topFactAMC = true;
          }, 0); // Force Fact & TP AMC
          setTimeout(() => {
            this.topTpAMC = true;
          }, 0);
          break;

        case dao.SituationSpecifique.SP081:
          console.log('Bénéficiaire Migrant de passage');
          break;

        case dao.SituationSpecifique.SP082:
          console.log('Bénéficiaire «autre situation de migrant»');
          setTimeout(() => {
            this.topTpAMO = false;
          }, 0);
          break;

        case dao.SituationSpecifique.SP09:
          console.log('Absence de support Vitale');
          break;

        case dao.SituationSpecifique.SP11:
          console.log('Forçage');
          break;

        case dao.SituationSpecifique.SP12:
          console.log('Professionnel de Santé utilisant les services d’un OCT');
          break;

        case dao.SituationSpecifique.CFE:
          console.log('Situation CFE');
          break;

        default:
          console.error('Situation non reconnue');
          break;
      }
    }
    let beneficiaire: dao.Beneficiaire | undefined;
    if (this.activeRecipientIndex !== undefined) beneficiaire = this.empreinteCVInserted?.beneficiaire[+this.activeRecipientIndex];
    console.log(`[onSituationNatureChange] built this.activeRecipientIndex: ${this.activeRecipientIndex}, \nbeneficiaire: ${JSON.stringify(beneficiaire, null, 2)} \nthis.empreinteCVInserted: ${JSON.stringify(this.empreinteCVInserted, null, 2)}`);
    if (typ_pj_amo == constants.TYP_PJ_AMO_CV && beneficiaire && this.empreinteCVInserted) {
      // beneficiaire or situation could have changed => change dsDTO
      this.bsDTO = dao.BeneficiaireFact.fromEmpreinteCV(sns, beneficiaire, this.empreinteCVInserted, sn.referenceDate);
      console.log(`[onSituationNatureChange] built bsDTO: ${JSON.stringify(this.bsDTO, null, 2)}`);
    }
    /* This crashed */
    if (this.empreinteADR && typ_pj_amo == constants.TYP_PJ_AMO_ADR) {
      //console.log(`%c[onSituationNatureChange] Calling specificCasesDetection`, "color: red;");
      //this.bsDTO = dao.BeneficiaireFact.fromEmpreinteADR(sns, beneficiaire, this.empreinteCVInserted, sn.referenceDate);
    }
  }

  onDatePrescriptionChange(newDate: string) {
    // Run stateService detection at given date
    console.log('[onDatePrescriptionChange] newDate', newDate ?? 'UNDEFINED');
    // 1. Guard against null/undefined indices early
    if (this.activeRecipientIndex === undefined || this.activeRecipientIndex === null) {
      console.error(`Invalid state: index ${this.activeRecipientIndex}`);
      return;
    }

    // 2. Ensure the parent object exists before attempting to index its arrays
    if (this.empreinteCVInserted && this.empreinteCVInserted.beneficiaire) {
      const beneficiaire = this.empreinteCVInserted.beneficiaire[+this.activeRecipientIndex];
      const referenceDate: Date = utils.isValidDate(newDate) ? utils.fromFRformat(newDate) : this.getReferenceDate();

      if (referenceDate && beneficiaire) {
        this.stateService.specificCasesDetection(beneficiaire, this.empreinteCVInserted.codeRegime, this.empreinteCVInserted.codeGest, referenceDate, 0);
      }
    }
  }

  /**
   * Returns the reference date for rights and situation nature calculation.
   * Uses prescription date if present and valid, otherwise falls back to invoice date (or formatted today).
   * @param referenceDate_s Optional date string in FR format
   * @returns {Date} Date object for rights calculation
   */
  public getReferenceDate(referenceDate_s?: string): Date {
    const dateStr = this.getReferenceDateString(referenceDate_s);
    return utils.fromFRformat(dateStr);
  }

  /**
   * Returns the reference date string in FR format (dd/mm/yyyy).
   * Uses referenceDate_s if valid, or prescription date if valid, otherwise falls back to invoice date (or formatted today).
   * @param referenceDate_s Optional date string in FR format
   * @returns {string} Date string in FR format
   */
  public getReferenceDateString(referenceDate_s?: string): string {
    if (referenceDate_s && utils.isValidDate(referenceDate_s)) {
      return referenceDate_s;
    }
    if (this.factureFR?.prescription?.datePresc && utils.isValidDate(this.factureFR.prescription.datePresc)) {
      return this.factureFR.prescription.datePresc;
    }
    if (this.factureFR?.dateFacture && utils.isValidDate(this.factureFR.dateFacture)) {
      return this.factureFR.dateFacture;
    }
    return this.formattedDate || formatter.format(new Date());
  }

  // Change in PJ (AMO or AMC) => Update bsDTO
  onPieceJustifChange() {
    console.log(`[onpieceJustifChange] current AMO / AMC: ${this.bsDTO.attestationAMO.pieceJustifAMO} / ${this.bsDTO.attestationAMC.pieceJustifAMC}`);
    console.log(`[onpieceJustifChange] this.empreinteCVInserted / this.empreinteADR:\r\n${JSON.stringify(this.empreinteCVInserted, null, 2)}\r\n${JSON.stringify(this.empreinteADR, null, 2)}`);
    //console.log(`[onpieceJustifChange] this.activeRecipientIndex: ${this.activeRecipientIndex}`);
    const referenceDate: Date = this.getReferenceDate();
    const selectedPieceJustifAMC = this.bsDTO.attestationAMC.pieceJustifAMC; // Keep selected pieceJustifAMC setting
    // First crate bsDTO 'header' + attestationAMO from CV, ADRi or attestation
    if (this.empreinteCVInserted && this.bsDTO.attestationAMO.pieceJustifAMO == dao.PieceJustifAMO.CARTE_VITALE && this.activeRecipientIndex !== undefined) {
      // Create from CV
      console.log(`%c[onPieceJustifChange] Create from CV`, 'color: brown;');
      const beneficiaire: dao.Beneficiaire = this.empreinteCVInserted.beneficiaire[+this.activeRecipientIndex];
      this.bsDTO = dao.BeneficiaireFact.fromEmpreinteCV(this.sns, beneficiaire, this.empreinteCVInserted, referenceDate);
      if (!this.topFactAMC) {
        // No AMC => clear AttestationAMC
        this.bsDTO.attestationAMC = dao.AttestationAMC.createDefault(); // Clear attestationAMC
      } else if (this.empreinteCVInserted && selectedPieceJustifAMC == '4') {
        // Change AMC to CV
        this.bsDTO.attestationAMC = dao.AttestationAMC.fromBeneficiaire(this.sns, this.empreinteCVInserted.beneficiaire[+this.activeRecipientIndex], constants.TYP_PJ_AMO_CV); // Get attestationAMC from CV
      } else if (this.empreinteADR && this.bsDTO.attestationAMC.pieceJustifAMC == 'A') {
        // Change AMC to ADRi
        this.bsDTO.attestationAMC = dao.AttestationAMC.fromBeneficiaire(this.sns, this.empreinteADR.beneficiaire, constants.TYP_PJ_AMO_ADR); // Get attestationAMC from ADRi
      } else {
        // Attestation change AMC to selected AMC pj
        console.log(`  Get PJ AMC back to ${selectedPieceJustifAMC}`);
        this.bsDTO.attestationAMC.pieceJustifAMC = selectedPieceJustifAMC;
      }
    } else if (this.empreinteADR && [dao.PieceJustifAMO.ADR, dao.PieceJustifAMO.APCV].includes(this.bsDTO.attestationAMO.pieceJustifAMO)) {
      // Create from ADRi call or ApCV?
      console.log(`%c[onPieceJustifChange] Create from ADRi call or ApCV`, 'color: brown;');
      this.bsDTO = dao.BeneficiaireFact.fromEmpreinteADR(this.sns, this.empreinteADR, referenceDate, this.bsDTO.attestationAMO.pieceJustifAMO); // Init specif beneficiaire w/ ADRi values
      this.bsDTO.attestationAMC = dao.AttestationAMC.createDefault(); // Clear attestationAMC
      if (this.empreinteCVInserted && selectedPieceJustifAMC == '4' && this.activeRecipientIndex !== undefined) {
        // Change AMC to CV
        this.bsDTO.attestationAMC = dao.AttestationAMC.fromBeneficiaire(this.sns, this.empreinteCVInserted.beneficiaire[+this.activeRecipientIndex], constants.TYP_PJ_AMO_CV); // Get attestationAMC from CV
      } else if (selectedPieceJustifAMC == 'A') {
        // Change AMC to ADRi
        this.bsDTO.attestationAMC = dao.AttestationAMC.fromBeneficiaire(this.sns, this.empreinteADR.beneficiaire, constants.TYP_PJ_AMO_ADR); // Get attestationAMC from ADRi
      } else {
        // Attestation change AMC to selected AMC pj
        console.log(`  Get PJ AMC back to ${selectedPieceJustifAMC}`);
        this.bsDTO.attestationAMC.pieceJustifAMC = selectedPieceJustifAMC;
      }
    } else {
    } // Attestation => keep bsDTO
    console.log('[onpieceJustifChange] new bsDTO: ', JSON.stringify(this.bsDTO, null, 2));
  }

  /**
   * Maps and sanitizes raw patient data into a unified identity format.
   * * This method handles the extraction logic for two primary streams:
   * 1. Physical Vitale Card (CV): Extracts data from the selected beneficiary in the card footprint.
   * 2. Mobile App Vitale (ApCV): Extracts data from the authentication context (QR/NFC).
   * * It ensures field consistency (e.g., date formatting, string conversion for IDs)
   * so that the rest of the application can consume `currentPatientIdentity`
   * without worrying about the source mode.
   * * @returns {void} Sets `this.currentPatientIdentity` or triggers an error UI if data is malformed.
   */
  private normalizePatientData(): void {
    // Determine currentPatientIdentity
    const mode = this.dialogdata.outputDialogData.mode;
    const data = this.dialogdata.outputDialogData;

    if (mode === constants.MODE_FSE_CV && data.empreinteCV && data.index !== undefined) {
      // Mode CV
      const b = data.empreinteCV.beneficiaire[data.index];
      this.currentPatientIdentity = {
        numSecu: data.empreinteCV.numSecu,
        dateNaissance: utils.toFRformat(b.dateNaissance),
        rangNaissance: b.rangNaissance,
        codeRegime: data.empreinteCV.codeRegime,
        caisseGest: data.empreinteCV.caisseGest.toString(), // Ensure string
        centreGest: data.empreinteCV.centreGest.toString(),
        nom: b.nomUsuel || b.nomFamille,
        prenom: b.prenom,
        qualite: b.qualite,
      };
    } else if ((mode === constants.MODE_FSE_APCV_QR || mode === constants.MODE_FSE_APCV_NFC) && data.contexteAuthentificationApCV) {
      // Mode ApCV
      const ctx = data.contexteAuthentificationApCV;
      let individu: dao.IndividuApCV;
      if (Array.isArray(ctx.individu)) {
        individu = ctx.individu[data.index ?? 0];
      } else {
        this.showError('Incohérence de données IndividuApCV'); // Should NOT happen
        return;
      }
      if (individu) {
        this.currentPatientIdentity = {
          numSecu: individu.numSecu,
          dateNaissance: utils.toFRformat(individu.dateNaissance),
          rangNaissance: parseInt(individu.rangNaissance, 10),
          codeRegime: individu.codeRegime,
          caisseGest: individu.caisseGest,
          centreGest: individu.centreGest,
          nom: individu.nomUsage || individu.nomFamille,
          prenom: individu.prenom,
          qualite: parseInt(individu.qualite, 10),
        };
      } else {
        this.currentPatientIdentity = undefined; // Should NOT happen
      }
    } else if (mode === constants.MODE_FSE_DEGRADE && this.dialogdata.initialPatientData) {
      const init = this.dialogdata.initialPatientData;
      this.currentPatientIdentity = {
        numSecu: init.nir || '',
        dateNaissance: init.dateNaissance || '',
        rangNaissance: init.rangNaissance ?? 1,
        codeRegime: init.codeRegime || '01',
        caisseGest: init.caisseGest || '',
        centreGest: init.centreGest || '',
        nom: init.nom || '',
        prenom: init.prenom || '',
        qualite: init.qualite ?? 0,
      };
    }
  }

  onAdriModeChange(_newValue: string) {
    // Save modeAppelAdri in c_station.data
    console.log(`${this.previousModeAppelAdri} -> ${this.modeAppelAdri}`);
    this.patientService.postStationUpdateModeAdri$(this.dialogdata.server_url, { station: 1, mode: this.modeAppelAdri }).subscribe({
      next: (_res: any) => {
        this.previousModeAppelAdri = this.modeAppelAdri;
        let station: dao.Station | undefined = this.dialogdata?.station;
        if (station) {
          station.data.configarea.mode_appel_adri = this.modeAppelAdri;
        }
        this.openInfoDialog(true);
      },
      error: e => {
        console.error('ERREUR Serveur : ', e.error);
        alert('ERREUR Serveur : ' + e.error);
        this.modeAppelAdri = this.previousModeAppelAdri; // Force previous value
      },
    });
  }

  onTypamcChange() {
    // console.log("onTypamcChange: ", this.typamc);
  }

  onBlurCentreGest() {
    this.bsDTO.attestationAMO.centreGest = this.bsDTO.attestationAMO.centreGest.padStart(4, '0');
  }

  onBlurRegime() {
    this.fse
      .recupererListeLibellesTauxExonerations(this.dialogdata.area_baseurl, this.bsDTO.attestationAMO.regime, this.dialogdata.outputDialogData.numIdentFact)
      .pipe(take(1))
      .subscribe({
        next: (listeLibellesTaux: dao.ListeLibellesTaux) => {
          console.log('onBlurRegime: ', JSON.stringify(listeLibellesTaux, null, 2));
          this.listeLibellesTaux = listeLibellesTaux.libelleTaux;
        },
        error: err => {
          console.error('Erreur lors de la récupération des libellés:', err);
          this.listeLibellesTaux = [];
          alert('Régime inconnu : ' + this.bsDTO.attestationAMO.regime);
        },
      });
  }

  onBlurMutnum() {
    if (this.bsDTO.attestationAMC.mutnum == '75500017') {
      this.msgAME = 'AME detectée';
    } else {
      this.msgAME = '';
    }
  }

  onBlurNIR() {
    console.log('Salut from : on blur nir');
    // Détection d'un NIR potentiellement fictif : 1er digit quelconque, puis 6 "5" consécutifs
    const fictifRegex = /^.5{6}/;
    if (!this.bsDTO.numSecu || !fictifRegex.test(this.bsDTO.numSecu)) {
      console.log(`[onBlurNIR] NIR non fictif (regex): ${this.bsDTO.numSecu}`);
      return;
    }
    console.log('Salut from : nir fictif detecte');
    // Source unifiée : liste déjà en cache OU appel HTTP qui la met en cache
    const liste$: Observable<dao.ListeNirFictifs> = this.listeNirFictifs ? of(this.listeNirFictifs) : this.fse.recupererListeNirFictifs$(this.dialogdata.area_baseurl, this.dialogdata.outputDialogData.numIdentFact).pipe(tap((liste: dao.ListeNirFictifs) => (this.listeNirFictifs = liste)));

    liste$.pipe(take(1)).subscribe(() => {
      // Désabonnement automatique dès la 1ère valeur
      console.log('Salut from : sss');
      const nir: dao.NirFictif | undefined = this.findNirFictif(this.bsDTO.numSecu);
      if (nir) {
        console.log(`[onBlurNIR] Found matching NIR fictif: ${JSON.stringify(nir, null, 2)}`);
        this.factureFR.contexteAnonymisation = 'CTX_FICTIF';
        if (nir.patternNumIdentPresc) {
          // [EV148] La table 10 contient un numéro de prescripteur, intitialiser la prescription
          let numIdent = nir.patternNumIdentPresc;
          if (/^\d{8}$/.test(numIdent)) {
            // 8 chiffres → on calcule et ajoute la clé (9ème digit)
            numIdent += this.computeNumIdentPrescKey(numIdent);
          }
          this.factureFR.prescription.numIdentPresc = numIdent;
        }
        if (nir.dateNaiss) {
          // [EV148] La table 10 contient une date de naissance, initialiser dateNaissBenef
          this.bsDTO.dateNaissBenef = utils.toFRformat(nir.dateNaiss);
        }
      }
    });
  }

  /**
   * Calcule la clé (9ème digit) d'un numéro d'identification de prescripteur à 8 caractères.
   * Règle :
   *   - Numérotation des caractères de droite à gauche (rang 1 = caractère le plus à droite)
   *   - Rang pair × 1, rang impair × 2
   *   - Somme chiffre par chiffre de tous les produits
   *   - Clé = complément à 10 du chiffre des unités de cette somme
   *   - Lettres transposées en valeur numérique : A=1, B=2, …
   */
  private computeNumIdentPrescKey(num: string): string {
    // Étape 1 : numérotation droite→gauche, transposition lettre→chiffre, multiplication selon rang
    const productsString = num
      .split('')
      .reverse()
      .map((c, idx) => {
        const value = /[A-Z]/i.test(c)
          ? c.toUpperCase().charCodeAt(0) - 64 // A=1, B=2, …, Z=26
          : parseInt(c, 10);
        return idx % 2 === 0 ? value * 2 : value; // idx 0 ↔ rang 1 (impair) ↔ ×2 ; sinon ×1
      })
      .join('');

    // Étape 2 : somme chiffre par chiffre de tous les produits concaténés
    const sum = productsString.split('').reduce((s, d) => s + parseInt(d, 10), 0);

    // Étape 3 : clé = complément à 10 du chiffre des unités (% 10 final pour que units=0 → clé=0)
    const key = (10 - (sum % 10)) % 10;
    return key.toString();
  }

  showInfoMsg(msg: string) {
    this.infomsg = msg;
    this.infomsgHighlighted = true;
    setTimeout(() => {
      this.infomsgHighlighted = false;
    }, 3000); // 3 seconds
  }

  private showError(text: string, title: string = 'Erreur') {
    console.error(text);
    Swal.fire({
      icon: 'error',
      title: title,
      width: '24em',
      text: text,
    });
  }

  getQualite(index: number): string {
    //console.log("[getQualite] index - this.empreinteCVInserted?.beneficiaire[index]", index, JSON.stringify(this.empreinteCVInserted?.beneficiaire[+index]));
    return this.map_qualities.get(this.empreinteCVInserted?.beneficiaire[+index].qualite ? +this.empreinteCVInserted?.beneficiaire[+index].qualite : 'Inconnu');
  }

  getBeneficiaire(index: number): dao.Beneficiaire | undefined {
    // Beneficiaire from CV
    return this.empreinteCVInserted?.beneficiaire[+index];
  }

  public getFSEStateDescription(state: string): string {
    // FIXME Get FSE state description safely
    return state;
  }

  // Detect cases when we have to call ADRi, based on AMO situation
  public isCallADR(sns: dao.SituationNatures): boolean {
    //debugger;
    if (this.modeAppelAdri == 'M') {
      // Mode MANUAL => no brainer, exit
      return false;
    }
    if (this.modeAppelAdri == 'A') {
      // Mode AUTO => no brainer, always call ADRi
      return true;
    } else if (this.modeAppelAdri == 'C') {
      // Mode ciblé => some conditions
      //if (!this.stateService.currentSituationNatureCV.isDroitsAMO) {	// Droits fermés en mode C => call ADRi
      if (!sns[constants.TYP_PJ_AMO_CV].isDroitsAMO) return true;
    }
    //if (this.stateService.currentSituationNatureCV.isALD) {					// ALD => Call ADRi
    if (sns[constants.TYP_PJ_AMO_CV].isALD) {
      // ALD => Call ADRi
      return true;
    }
    if (
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP03a ||
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP03b ||
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP03c ||
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP03d ||
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP04 ||
      sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP06
      //|| (sns[constants.TYP_PJ_AMO_CV].sp == dao.SituationSpecifique.SP18)  // !! L'attentat ne provoque pas la lecture ADRi
    ) {
      return true;
    }
    return false;
  }

  public controlFacture(): utils.ProcessingResult {
    // First 'local' FSE control, NO Area query
    let res = new utils.ProcessingResult();
    const f = this.factureFR;
    if (f.dateRefAMC == '') res.addError('La date des soins doit être renseignée');
    if (!this.isPrescriptionComplete) {
      res.addError('Les informations de prescription obligatoires doivent être renseignées (Date, Id facturation, Spécialité, Type)');
    }
    if (this.bsDTO.attestationAMO.pieceJustifAMO != dao.PieceJustifAMO.CARTE_VITALE) {
      // Data does not come from CV
      let dateString = this.bsDTO.dateNaissBenef; // Contrôle dateNaissBenef
      dateString = dateString.replaceAll('-', '/'); // In case one types 21-12-2015
      dateString = dateString.replaceAll('.', '/'); // In case one types 21.12.2015
      var parts = dateString.split('/');
      if (parts.length !== 3) {
        res.addError('La date de naissance est incorrecte (jj/mm/aaaa)');
      } else {
        var month = parts[1]; // Month can be 19!
        if (parts.length !== 3) res.addError('La date de naissance doit être renseignée');
        if (utils.padZero(month, 2) == '00') res.addError("Le mois de naissance ne peut être '00'");
      }
    }
    return res;
  }

  public toFactureFR(f: dao.FactureFR): dao.FactureFR {
    // Data transfert this -> FactureFR
    f.topFactAMO = this.topFactAMO ? 1 : 0;
    f.topTpAMO = this.topTpAMO ? 1 : 0;
    f.topFactAMC = this.topFactAMC ? 1 : 0;
    f.topTpAMC = this.topTpAMC ? 1 : 0;
    //console.log('[controlFacture]', JSON.stringify(f, null, 2));
    return f;
  }

  public fromFactureFR(f: dao.FactureFR): void {
    // Data transfert FactureFR -> this
    this.topFactAMO = f.topFactAMO == 1;
    this.topFactAMC = f.topFactAMC == 1;
    this.topTpAMO = f.topTpAMO == 1;
    this.topTpAMC = f.topTpAMC == 1;
  }

  private findNirFictif(nir: string): dao.NirFictif | undefined {
    //console.log(`%c[findNirFictif] listeNirFictifs: ${JSON.stringify(this.listeNirFictifs, null, 2)}`, "color: brown;");

    if (!this.listeNirFictifs || !Array.isArray(this.listeNirFictifs.nirFictif)) {
      console.error('Erreur de format de la liste des Nir Fictifs');
      return undefined;
    }
    let normalizedNir = String(nir).replace(/\s|-/g, ''); // Normalise input: remove spaces and common separators (adjust if needed)
    if (normalizedNir.length !== 15) return undefined; // NIR = 13 + 2 (key)
    normalizedNir = normalizedNir.slice(0, -2); // Remove key
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Helper to escape regex-special characters in non-placeholder runs

    //console.log(`%c[findNirFictif] normalizedNir: ${normalizedNir}`, "color: brown;");

    for (const entry of this.listeNirFictifs.nirFictif) {
      const pat = entry.patternNirFictif;
      if (!pat) continue;
      console.log(`%c  processing pattern: ${pat}`, 'color: brown;');
      let regexStr = '';
      // Build regex by scanning runs of 'C' vs other characters
      let i = 0;
      while (i < pat.length) {
        const ch = pat[i];
        if (ch === 'C') {
          // Count consecutive C's
          let j = i;
          while (j < pat.length && pat[j] === 'C') j++;
          const count = j - i;
          regexStr += `\\d{${count}}`;
          i = j;
        } else {
          // take a run of non-C chars to escape in one go
          let j = i;
          while (j < pat.length && pat[j] !== 'C') j++;
          const run = pat.slice(i, j);
          regexStr += escapeRegex(run);
          i = j;
        }
      }
      console.log(`%c  regex: ${regexStr}`, 'color: brown;');
      const regex = new RegExp(`^${regexStr}$`);
      if (regex.test(normalizedNir)) {
        return entry;
      }
    }
    return undefined;
  }

  /**
   * Checks whether all mandatory prescription fields are properly filled.
   * Mandatory fields: numIdentPresc, codeSpecPresc, typePresc, and a valid datePresc.
   */
  get isPrescriptionComplete(): boolean {
    const p = this.factureFR?.prescription;
    return !!(p && p.numIdentPresc && p.numIdentPresc.trim() !== '' && p.codeSpecPresc && p.codeSpecPresc.trim() !== '' && p.typePresc && p.typePresc.trim() !== '' && p.datePresc && utils.isValidDate(p.datePresc));
  }
}
